/**
 * ConfirmDialog custom — alternative à `window.confirm()` pour les actions
 * destructives ou sensibles.
 *
 * Usage :
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Exclure", message: "...", danger: true })) {
 *     mutation.mutate();
 *   }
 *
 * Le hook retourne une Promise<boolean> : `true` si l'utilisateur confirme,
 * `false` si annulation (ESC, click hors modale, bouton Annuler).
 */

import { Button } from "@champ-erp/ui";
import { AlertTriangle } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  /** Style danger (icône rouge + bouton rouge). Pour les actions irréversibles. */
  danger?: boolean;
  /** Texte du bouton de confirmation. Défaut : "Confirmer". */
  confirmLabel?: string;
  /** Texte du bouton d'annulation. Défaut : "Annuler". */
  cancelLabel?: string;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setRequest({ ...options, resolve });
    });
  }, []);

  const handleClose = useCallback(
    (ok: boolean) => {
      if (!request) return;
      request.resolve(ok);
      setRequest(null);
    },
    [request],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request &&
        createPortal(<ConfirmModal request={request} onClose={handleClose} />, document.body)}
    </ConfirmContext.Provider>
  );
}

function ConfirmModal({
  request,
  onClose,
}: {
  request: ConfirmRequest;
  onClose: (ok: boolean) => void;
}) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // Focus initial sur le bouton confirmer pour permettre Enter direct.
  // Pour les actions danger, on focus le bouton Annuler à la place pour
  // éviter qu'un Enter mal placé déclenche une suppression.
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (request.danger) {
      cancelBtnRef.current?.focus();
    } else {
      confirmBtnRef.current?.focus();
    }
  }, [request.danger]);

  // ESC ferme la modale (équivalent Annuler)
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") onClose(false);
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4"
      onClick={() => onClose(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          {request.danger && (
            <div className="mt-0.5 shrink-0 rounded-full bg-rust-100 p-2 text-rust-700">
              <AlertTriangle size={20} />
            </div>
          )}
          <div className="flex-1">
            <h2 id="confirm-title" className="text-lg font-semibold text-aubergine-900">
              {request.title}
            </h2>
            <div className="mt-2 text-sm text-neutral-700">{request.message}</div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button ref={cancelBtnRef} type="button" variant="ghost" onClick={() => onClose(false)}>
            {request.cancelLabel ?? "Annuler"}
          </Button>
          <Button
            ref={confirmBtnRef}
            type="button"
            onClick={() => onClose(true)}
            variant={request.danger ? "danger" : "primary"}
          >
            {request.confirmLabel ?? "Confirmer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm() doit être utilisé sous <ConfirmDialogProvider>");
  return ctx;
}
